import { EventEmitter, Injectable, ElementRef, Renderer2 } from '@angular/core';
import { APP_CONFIG, Env, Region } from '../../environment';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { catchError, delay, map, retryWhen, take } from 'rxjs/operators';
import { COUNTRY_SETTINGS, FEATURES, PASSWORD_MIN_LENGTH, PATTERNS, COUNTRY_SETTINGS_ENABLE_VALUE, LANGUAGES, PHONE_VERIFICATION_EVENT_LOCATION, OTP_ACTIONS, PAGES, REGISTRATION_RESOURCE_TYPE, PromiseStatus, DIGITAL_LICENSE, DELIVERY_TYPE } from './app-variables';
import { HttpMapper } from './http-mapper';
import { FormControlProvider } from '../../providers/formbuilder/formcontrol';
import { StorageProvider } from '../../providers/storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { Content, ModalController, Platform, AlertController } from 'ionic-angular';
import { RouteProvider } from '../../providers/route/route';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { SegmentProvider } from '../analytics/segment';
import { getSourceAnalyticsData } from '../../providers/analytics/deeplink-source';
import { AnalyticsEventType, AnalyticsPhoneEventsVerification, SegmentLocationNames } from '../../model/types/analytics.const';
import { LoadingProvider } from '../../providers/loading/loading';
import { OTPHeaderResponse } from '../../model/models/auth/otp.interface';
import { HttpProvider } from '../http/http';
import { FormControl } from '@angular/forms';
import { AccountProvider } from '../../providers/myria/account-provider';
import { RmtTranslateComponent } from '../../components/rmt-translate/rmt-translate';
import { regions } from '../../environment/region/regions';
import { readAutomationCountryOverride } from './automation-country';
import jwtDecode from 'jwt-decode';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Branding } from '../../environment/branding';
import { ResourceProvider } from '../resource/resource';
import { PaymentMethodType } from '../../model/types/payment-method-type';
import { NumberFormatter } from '../../pipes/currency-pipe';
import { IDestinationCountrySettingsResponse } from '../../model/api/country/destination-country-settings-response.interface';
import { FeatureFlagTypes, Rmt5FlagMode, isRmt5FlagMode, rmt5ForcedValue } from '../../model/types/feature-flag-types';
import { EKYC_PAGEFROM } from '../../model/models/ekyc-singpass/ekyc-singpass.model';
import { VueBridgeState } from '../../providers/vue/vue-helpers';
import { BridgeBankDepositCompleteData, BridgeBankDepositEntry } from '../../model/types/bridge-types';
import { ICustomer } from '../../model/models/customer/customer.interface';
import { INavInfo } from '../../model/models/layout/nav-info.interface';

// Static reference to avoid circular dependency
let _vueProviderInstance: any = null;

export { FeatureFlagTypes } from '../../model/types/feature-flag-types';

declare let navigator;
declare const AppRate;
@Injectable()
export class UtilsProvider {
  private features: any;
  private featuresObservable: Observable<any>;
  private customerFeaturesCountry: any = {};
  private customerSettingsCountry: any = {};
  public currentPageEmitter: EventEmitter<string> = new EventEmitter<string>();
  public showPendingTransactionDot: boolean;
  private emailRegex: RegExp;
  private mobileRegex: RegExp;
  public menuOpened: boolean;
  private deviceLanguage: any;
  private lastFeaturesCountryId: number | null = null;

  static tabs: any;

  static featureFlagOverrides: { [flag: string]: boolean | string } = {};
  static rmt5FlagMode: Rmt5FlagMode = 'off';

  // Keyed on the raw flag value, not the flag name, so a value that changes mid-session is a miss and
  // reparses on its own
  private static featureFlagListCache: { [rawValue: string]: string[] } = Object.create(null);

  static resolveFeatureFlagRaw(featureFlag: FeatureFlagTypes) {
    if (APP_CONFIG.env !== Env.PROD) {
      const forced = rmt5ForcedValue(featureFlag, UtilsProvider.rmt5FlagMode);
      if (forced !== null) {
        return forced;
      }
      if (UtilsProvider.featureFlagOverrides.hasOwnProperty(featureFlag)) {
        return UtilsProvider.featureFlagOverrides[featureFlag];
      }
    }
    return HttpProvider.featureFlags ? HttpProvider.featureFlags[featureFlag] : undefined;
  }

  static async loadFeatureFlagOverrides(storageProvider: StorageProvider) {
    if (APP_CONFIG.env === Env.PROD) {
      UtilsProvider.featureFlagOverrides = {};
      UtilsProvider.rmt5FlagMode = 'off';
      return;
    }
    const automationTest = UtilsProvider.readAutomationTestOverride();
    if (automationTest !== null) {
      APP_CONFIG.automationTest = automationTest;
    }
    const stored = await storageProvider.getJson(StorageType.FeatureFlagOverrides);
    UtilsProvider.featureFlagOverrides = stored && typeof stored === 'object' ? stored : {};
    // Precedence: automation override > stored QA selection > CI-baked default (APP_CONFIG.RMT5_LEGACY_MODE → 'legacy') > 'off'.
    const overrideMode = UtilsProvider.readRmt5ModeOverride();
    if (overrideMode) {
      UtilsProvider.rmt5FlagMode = overrideMode;
      return;
    }
    const storedMode = await storageProvider.getJson(StorageType.Rmt5FlagMode);
    UtilsProvider.rmt5FlagMode = isRmt5FlagMode(storedMode)
      ? storedMode
      : ((APP_CONFIG as any).RMT5_LEGACY_MODE === true ? 'legacy' : 'off');
  }

  // Automation hook (non-PROD, read at startup): set the mode without the QA menu.
  //   Native (Selenium/Appium in the WebView):  localStorage.setItem('rmt5Mode', 'legacy'); then reload.
  //   Browser convenience:                      ?rmt5=legacy anywhere in the URL.
  // Values: 'legacy' | 'off' | 'full'.
  static readRmt5ModeOverride(): Rmt5FlagMode | null {
    try {
      if (typeof window === 'undefined') {
        return null;
      }
      if (window.localStorage) {
        const stored = window.localStorage.getItem('rmt5Mode');
        const value = stored ? stored.toLowerCase() : null;
        if (isRmt5FlagMode(value)) {
          return value;
        }
      }
      if (window.location) {
        const match = (window.location.href || '').match(/[?&]rmt5=([^&#]+)/);
        const urlValue = match ? decodeURIComponent(match[1]).toLowerCase() : null;
        if (isRmt5FlagMode(urlValue)) {
          return urlValue;
        }
      }
      return null;
    } catch (ex) {
      return null;
    }
  }

  // Automation hook (non-PROD, read at startup): enable APP_CONFIG.automationTest without the QA menu.
  //   Native (Appium/Mobilewright in the WebView):  localStorage.setItem('automationTest', 'true'); then reload.
  //   Browser convenience:                          ?automationTest=true anywhere in the URL.
  // Values: 'true' | 'false' (case-insensitive); anything else is ignored and the build value stays.
  static readAutomationTestOverride(): boolean | null {
    const parse = (raw: string | null): boolean | null => {
      const value = raw ? raw.toLowerCase() : null;
      return value === 'true' ? true : value === 'false' ? false : null;
    };
    try {
      if (typeof window === 'undefined') {
        return null;
      }
      if (window.localStorage) {
        const value = parse(window.localStorage.getItem('automationTest'));
        if (value !== null) {
          return value;
        }
      }
      if (window.location) {
        const match = (window.location.href || '').match(/[?&]automationTest=([^&#]+)/);
        const urlValue = parse(match ? decodeURIComponent(match[1]) : null);
        if (urlValue !== null) {
          return urlValue;
        }
      }
      return null;
    } catch (ex) {
      return null;
    }
  }

  // Automation hook (non-PROD, read at startup): pin the pre-login country instead of the geo-IP one.
  //   Native (Appium/Mobilewright in the WebView):  localStorage.setItem('automationCountry', 'US'); then relaunch.
  //   Browser convenience:                          ?automationCountry=US anywhere in the URL.
  // See readAutomationCountryOverride() in automation-country.ts (kept separate so HttpProvider can use it without an import cycle).
  static readAutomationCountryOverride(): string | null {
    return readAutomationCountryOverride();
  }

  static async saveFeatureFlagOverrides(storageProvider: StorageProvider) {
    await storageProvider.setJson(StorageType.FeatureFlagOverrides, UtilsProvider.featureFlagOverrides);
    await storageProvider.setJson(StorageType.Rmt5FlagMode, UtilsProvider.rmt5FlagMode);
  }

  deeplinkRegistrationFlowType: string = '';
  deeplinkImpactRadiusName: string = 'None';
  isLREnabled: Boolean = false;
  disableLiteRegistration: Boolean = false;

  rmt5VueBridgeSettings: boolean = true;

  constructor(
    private storageProvider: StorageProvider,
    public http: HttpMapper,
    public translate: TranslateService,
    public formProvider: FormControlProvider,
    private routeProvider: RouteProvider,
    private platform: Platform,
    private analytics: SegmentProvider,
    private modal: ModalController,
    private loadingProvider: LoadingProvider,
    private httpProvider: HttpProvider,
    private accountProvider: AccountProvider,
    private resourceProvider: ResourceProvider,
    private alertCtrl: AlertController,
    private sanitizer: DomSanitizer,
    private httpClient: HttpClient,
    private currencyFormatter: NumberFormatter
  ) {
    this.emailRegex = new RegExp(PATTERNS.EMAIL);
    this.mobileRegex = new RegExp(PATTERNS.PHONE);
    this.deviceLanguage = this.getDeviceLanguage();
  }

  // Static method to set VueProvider from outside to avoid circular dependency
  static setVueProvider(vueProvider: any) {
    _vueProviderInstance = vueProvider;
  }

  // Getter for VueProvider
  private get vueProvider(): any {
    return _vueProviderInstance;
  }

  async navigateToReferralSignup(countryFrom: string, fallback: () => Promise<void>): Promise<void> {
    await this.httpProvider.initRemittanceAuthToken();
    if (this.vueProvider && await this.hasFeatureFlag(FeatureFlagTypes.Rmt5VueBridgeSignup)) {
      await this.vueProvider.navigate('RegistrationCountry', '', { countryFrom }, fallback, true);
      return;
    }
    await fallback();
  }

  async navigateToHomeOrFallback(fallbackPage: any): Promise<boolean> {
    if (this.vueProvider && typeof this.vueProvider.navigateToHomeOrFallback === 'function') {
      return await this.vueProvider.navigateToHomeOrFallback(fallbackPage);
    }
    if (fallbackPage) {
      await this.routeProvider.setRoot(fallbackPage);
    }
    return false;
  }

  async isSNNonPilotState(getCustomer: () => Promise<ICustomer>): Promise<boolean> {
    const snPilotStateEnabled = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.SNPilotStateEnabled);
    if (!snPilotStateEnabled)
      return false;
    try {
      const pilotStates: string[] = Array.isArray(snPilotStateEnabled) ? snPilotStateEnabled : JSON.parse(snPilotStateEnabled);
      if (!pilotStates.length)
        return false;
      const customer = await getCustomer();
      const userState = this.normalizeStateName(customer && customer.address ? customer.address.stateProvince : '');
      return !pilotStates.some(state => this.normalizeStateName(state) === userState);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to parse SN pilot state list feature flag or failed to get customer state from profile call');
      return false;
    }
  }

   // Last four digits of a (possibly masked) account or card number, e.g. '************0006' -> '0006'.
   // Returns '' when fewer than four digits are present.
  getLastFourDigits(accountNumber: string): string {
    const digits = accountNumber ? String(accountNumber).replace(/\D/g, '') : '';
    return digits.length >= 4 ? digits.slice(-4) : '';
  }

  normalizeStateName(state: string): string {
    return state ? String(state).replace(/\s/g, '').toLowerCase() : '';
  }

  navigateToBankDepositOrFallback(
    entry: BridgeBankDepositEntry,
    onComplete: (data: BridgeBankDepositCompleteData) => Promise<void> | void,
    onFallback: () => void,
  ): void {
    if (this.vueProvider && typeof this.vueProvider.navigateToBankDeposit === 'function') {
      this.vueProvider.navigateToBankDeposit(entry, onComplete, onFallback);
      return;
    }
    onFallback();
  }

  async hideVueBridgeIfVisible(): Promise<void> {
    if (VueBridgeState.isUserInsideVueBridge && this.vueProvider && typeof this.vueProvider.hideVueBridgeIframe === 'function') {
      await this.vueProvider.hideVueBridgeIframe();
    }
  }

  getCountriesFeatures(): Observable<any> {
    return this.getFeatures().pipe(map((features: any) => features.CountriesFeatures));
  }

  getAgentFeatures(): Observable<any> {
    return this.getFeatures().pipe(map((features: any) => features.AgentFeatures));
  }

  registerEvent(eventId: number): Observable<any> {
    let params = {
      EventId: eventId
    };
    return this.http.post(APP_CONFIG.API_URL + 'api/Events', params);
  }

  getCountrySettings(countryId: number): Observable<any> {
    if (this.customerSettingsCountry[countryId]) {
      return Observable.of(this.customerSettingsCountry[countryId]);
    } else {
      return this.getCountriesFeatures().pipe(map((features: any) => {
        for (let country of features) {
          if (country.CountryId == countryId) {
            this.customerSettingsCountry[countryId] = country.CountrySettings;
            return country.CountrySettings;
          }
        }
        LoggerProvider.error(this, LoggerEvent.ProviderException, `!!! Missing country settings for ${countryId} !!!`);
        this.customerSettingsCountry[countryId] = [];
        return [];
      }));
    }
  }

  getCountryFeatures(countryId: number): Observable<any> {
    if (this.customerFeaturesCountry[countryId]) {
      return Observable.of(this.customerFeaturesCountry[countryId]);
    } else {
      return this.getCountriesFeatures().pipe(map((features: any) => {
        for (let country of features) {
          if (country.CountryId == countryId) {
            this.customerFeaturesCountry[countryId] = country.Features;
            return country.Features;
          }
        }
        this.customerFeaturesCountry[countryId] = [];
        return [];
      }));
    }
  }

  hasCountryFeature(countryId: number, feature: FEATURES): Observable<boolean> {
    return this.getCountryFeatures(countryId).pipe(map((features: any) => ((features && features.indexOf(feature)) >= 0 ? true : false)));
  }

  hasCountrySetting(countryId: number, setting: COUNTRY_SETTINGS): Observable<any> {
    return this.getCountrySettings(countryId).pipe(map((countrysettings: any) => {
      let resul;
      for (let countrysetting of countrysettings) {
        if (countrysetting.SettingType == setting) {
          resul = countrysetting.SettingValue;
        }
      }
      return resul;
    }));
  }

  hasCountrySettingByPromise(countryId: number, setting: COUNTRY_SETTINGS): Promise<any> {
    return new Promise((resolve, reject) => {
      this.hasCountrySetting(countryId, setting).subscribe(setting => resolve(setting), err => reject(err));
    });
  }

  // Refreshes ONLY the api/Resources FeaturesTask
  // then clears the in-memory feature cache and re-reads via the existing feature machinery.
  async forceRefreshCountrySetting(countryId: number, setting: COUNTRY_SETTINGS): Promise<boolean> {
    try {
      await this.resourceProvider.refreshFeaturesTask(countryId);
      // Clear our in-memory feature so the next read picks up the freshly refreshed FeaturesTask
      await this.refreshCountryFeatures();
      const value = await this.hasCountrySettingByPromise(countryId, setting);
      return this.normalizeToBoolean(value);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'forceRefreshCountrySetting failed for setting ' + setting);
      return false;
    }
  }

  hasCountryFeatureByPromise(countryId: number, feature: FEATURES): Promise<any> {
    return new Promise((resolve, reject) => {
      this.hasCountryFeature(countryId, feature).subscribe(feature => resolve(feature), err => reject(err));
    });
  }

  refreshCountryFeatures(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.cleanCountryFeatures();
      this.getFeatures().subscribe(() => resolve(true), err => reject(err));
    });
  }

  cleanCountryFeatures() {
    this.features = undefined;
    this.featuresObservable = undefined;
    this.customerFeaturesCountry = {};
    this.customerSettingsCountry = {};
  }

  hasCountrySettings(countryId: number, settings: COUNTRY_SETTINGS[]): Observable<any>{
    let observables = [];
    settings.forEach( setting =>{
      observables.push(this.hasCountrySetting(countryId,setting));
    });

    return  Observable.forkJoin(observables);
  }

  applyPilotSettings(countrySettings) {
    SegmentProvider.isPilotUser = false;

    try {
      let pilotDevices: any = null;
      let pilotSettings: any = null;

      const featureMap = {}
      for (let countrysetting of countrySettings) {
        if (countrysetting.SettingType == COUNTRY_SETTINGS.PILOT_DEVICES)
          pilotDevices = countrysetting.SettingValue.split('|');
        if (countrysetting.SettingType == COUNTRY_SETTINGS.PILOT_COUNTRY_SETTINGS)
          pilotSettings = countrysetting.SettingValue;
        featureMap[countrysetting.SettingType] = countrysetting;
      }
      if (pilotDevices) {
        let version = this.httpProvider.getHeaderVersion();
        for (let i=0; i < pilotDevices.length; i++) {
          let device = pilotDevices[i];
          let identifiedby = '???';
          let apply = false;
          if (device === 'android') {
            apply = (this.platform.is('android')) ? true : false;
            identifiedby = 'Android device';
          }
          else if (device === 'ios') {
            apply = (this.platform.is('ios')) ? true : false;
            identifiedby = 'iOS device';
          }
          else if (device === LoggerProvider.uuid) {
            apply = true;
            identifiedby = 'device ID match';
          }
          else {
            identifiedby = 'device ID regex';

            if (device.indexOf('@@') > 0) {
              const parts = device.split('@@');
              if (parts.length === 2) {
                const targetVersion = parts[0];
                device = null;
                if (this.httpProvider.isNewerVersion(targetVersion, version, true)) {
                  identifiedby = `version with device ID regex with version ${version} >= ${targetVersion}`;
                  device = parts[1];
                }
              }
            }
            if (device) {
              const reg = new RegExp(`^${device}$`);
              apply = reg.test(LoggerProvider.uuid);
            }
          }

          if (apply) {
            SegmentProvider.isPilotUser = true;
            LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Device will use pilot settings identified by ' + identifiedby, pilotSettings);
            const settings = pilotSettings.split('|');
            settings.forEach((value) => {
              let loginfo = '';
              if (value.indexOf('@@') > 0) {
                const parts = value.split('@@');
                if (parts.length === 2) {
                  const targetVersion = parts[0];
                  if (!this.httpProvider.isNewerVersion(targetVersion, version, true)) {
                    LoggerProvider.info(this, `Pilot setting skipped because version is too old ${version} < ${targetVersion} for ${parts[1]}`)
                    return;
                  }
                  loginfo = ` (version ${version} >= ${targetVersion})`;
                  value = parts[1];
                }
              }
              const values = value.split(':');
              if (values.length === 2) {
                const setting = featureMap[values[0]];
                if (setting) {
                  LoggerProvider.info(this, `Pilot setting override for ${values[0]} from ${setting.SettingValue} to ${values[1]}` + loginfo)
                  setting.SettingValue = values[1]
                }
                else
                  LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Unknown pilot setting ' + values[0] + loginfo);
              }
              else
                LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Invalid pilot setting ' + value + loginfo);
            });
            break;
          }
        }
      }
      return true;
    } catch(ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Exception during pilot settings');
    }
    return false;
  }

  getFeatures(userFeatures?: boolean, repeated: boolean = false): Observable<any> {
    if (this.features) {
      return Observable.of(this.features);
    } else if (this.featuresObservable) {
      return this.featuresObservable;
    } else if (typeof userFeatures == 'undefined') {
      this.featuresObservable = Observable.fromPromise(new Promise(async(resolve, reject) => {
        const countryId = APP_CONFIG.region.regionId;
        // Avoid forcing resources reload on first run; only reload when country actually changes to avoid unnecessary API calls. Ensure forceReload when country changes to avoid breaking Pin signin countriess like SN - OM-17727
        let forceReload = false;
        if (this.lastFeaturesCountryId === null) {
          this.lastFeaturesCountryId = countryId;
        } else if (this.lastFeaturesCountryId !== countryId) {
          forceReload = true;
          this.lastFeaturesCountryId = countryId;
          // OM-17799: Clear cached country settings when country changes to avoid empty arrays
          this.customerFeaturesCountry = {};
          this.customerSettingsCountry = {};
        }

        try {
          const defaultResponse = { CountriesFeatures: [], CountrySettings: [], AgentFeatures: [] };
          if(this.httpProvider.pendingUpdate) {
            delete this.featuresObservable;
            this.features = null;
            resolve(defaultResponse)
            return;
          }
          const resources = await this.resourceProvider.getResources(forceReload);
          let features: any;
          if (!this.resourceProvider.useResourcesEndpoint) {
            features = await this.httpProvider.get(
              APP_CONFIG.API_URL +
              'api/Features?CountryId=' +
              countryId +
              '&ignoreCache=true'
            );
          } else features = resources.FeaturesTask;
          delete this.featuresObservable;

          // OM-17799: Set features before mismatch check
          this.features = features;
          // OM-17799: Clear cached country settings to ensure fresh data from new features
          this.customerFeaturesCountry = {};
          this.customerSettingsCountry = {};

          if (countryId !== APP_CONFIG.region.regionId) {
            LoggerProvider.error(this, LoggerEvent.ProviderException, 'Country changed for country settings: ' + countryId + ' <=> ' + APP_CONFIG.region.regionId);
            if (repeated) {
              LoggerProvider.error(this, LoggerEvent.ProviderException, 'Repeated country settings error');
            } else {
              // The condition (countryId !== APP_CONFIG.region.regionId) is true because APP_CONFIG.region.regionId changes during the async for parseFeatureFlags.
              // This leads to a duplicate API call to api/Features with the same countryId, which isn't necessary.
              // I've temporarily commented out that condition to prevent the repeated call, this section to be refactoed in Phase 2
              // await this.getFeatures(userFeatures, true).toPromise();
              // OM-17799: Return the features we just set (not empty defaultResponse)
              resolve(this.features);
              return;
            }
          }

          let countrySettings: any = null;
          if (this.features && this.features.CountriesFeatures && this.features.CountriesFeatures.length > 0) {
            const countryFeatures = this.features.CountriesFeatures.find((country) => (country.CountryId == APP_CONFIG.region.regionId))
            if (countryFeatures && countryFeatures.CountrySettings && countryFeatures.CountrySettings.length > 0)
              countrySettings = countryFeatures.CountrySettings;
          }
          if (countrySettings) {
            if (!this.platform.is('cordova')) {
              // to really test it change it to Virtual2.* since Virtual UUIDs always start with Virtual2022*
              countrySettings.push({
                SettingType: COUNTRY_SETTINGS.PILOT_DEVICES,
                SettingValue: 'Virtual0.*|Virtual1.*'
              });
              countrySettings.push({
                SettingType: COUNTRY_SETTINGS.PILOT_COUNTRY_SETTINGS,
                SettingValue: '49000:1|93000:1'
              });
            }
            this.applyPilotSettings(countrySettings);
          } else {
            LoggerProvider.error(this, LoggerEvent.ProviderException, 'Failed to get country settings from features');
            delete this.featuresObservable;
            this.features = null;
            reject(new Error('Missing country settings for region ' + APP_CONFIG.region.regionId))
            return;
          }
          this.checkDefaultTranslations();
          await this.checkIsLiteRegistrationSettingEnabled();
          resolve(this.features);
        } catch (error) {
          LoggerProvider.exception(this, error, LoggerEvent.ProviderException, 'Exception during get country features');
          delete this.featuresObservable;
          reject(error)
        }
      }));
      return this.featuresObservable;
    } else {
      this.featuresObservable = Observable.fromPromise(new Promise(async(resolve, reject) => {
        try {
          delete this.featuresObservable; // when the cached features is available we don't need the `featuresObservable` reference anymore
          const resources = await this.resourceProvider.getResources();
          if (!this.resourceProvider.useResourcesEndpoint)
            resources.FeaturesTask = await this.httpProvider.get(APP_CONFIG.API_URL + 'api/Features?CountryId=' + APP_CONFIG.region.regionId + "&ignoreCache=true") // added ignoreCache parameter to avoid the issue we have with cache for newly added country settings. This change doses't need to got to production.
          this.features = resources.FeaturesTask;
          resolve(this.features);
        } catch (error) {
          LoggerProvider.exception(this, error, LoggerEvent.ProviderException, 'Exception during get country features');
          delete this.featuresObservable;
          reject(error);
        }
      }));
      return this.featuresObservable;
    }
  }

  async getDestinationCountrySettings(countryId?: number, settingId?: COUNTRY_SETTINGS): Promise<IDestinationCountrySettingsResponse> {
    return this.httpProvider.get(APP_CONFIG.API_URL + 'api/Features/GetCountrySettingById?CountryId=' + countryId + "&settingID=" + settingId + "&ignoreCache=true");
  }

  getSettingsByCountryId(countryId?: number): Observable<any> {
    return Observable.fromPromise(new Promise((resolve, reject) => {
      this.httpProvider.get(APP_CONFIG.API_URL + 'api/Features?CountryId=' + countryId + "&ignoreCache=true")
        .then((features) => {
          try {

            let countrySettings;
            if (features && features.CountriesFeatures && features.CountriesFeatures.length > 0) {
              const countryFeatures = features.CountriesFeatures.find((country) => (country.CountryId == countryId))
              if (countryFeatures && countryFeatures.CountrySettings && countryFeatures.CountrySettings.length > 0)
                countrySettings = countryFeatures.CountrySettings;
            }
            if (countrySettings) {
              if (!this.platform.is('cordova')) {
                // to really test it change it to Virtual2.* since Virtual UUIDs always start with Virtual2022*
                countrySettings.push({
                  SettingType: COUNTRY_SETTINGS.PILOT_DEVICES,
                  SettingValue: 'Virtual0.*|Virtual1.*'
                });
                countrySettings.push({
                  SettingType: COUNTRY_SETTINGS.PILOT_COUNTRY_SETTINGS,
                  SettingValue: '49000:1|93000:1'
                });
              }
            }
            resolve(countrySettings);
            // this.checkDefaultTranslations();
            // await this.checkIsLiteRegistrationSettingEnabled();
          } catch (error) {
            LoggerProvider.exception(this, error, LoggerEvent.ProviderException, 'Exception during get country settings');
            reject(error)
          }
        });
    }).catch((error: any) => {
        LoggerProvider.exception(this, error, LoggerEvent.ProviderException, 'Exception during get country settings');
      })
    )
  }

  async checkDefaultTranslations() {
    if (!APP_CONFIG.region.defaultTranslationParams) {
      APP_CONFIG.region.defaultTranslationParams = {
        contactPhone: await this.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.CUSTOMERS_SERVICE_NUMBER),
      };
    }
  }

  validatePassword(passwod: string): any {
    let message: string;
    if (!passwod || passwod.length < PASSWORD_MIN_LENGTH || !/[0-9]/.test(passwod) || !/[a-z]/.test(passwod) || !/[A-Z]/.test(passwod)) {
      message = 'PasswordInvalid';
    }
    return { value: message ? false : true, message: message };
  }

  joinParams(firstParam: any, secondParam: any, thirdParam?: any): string {
    let array = new Array<string>();
    if (firstParam) array.push(firstParam);
    if (secondParam) array.push(secondParam);
    if (thirdParam) array.push(thirdParam);
    return array.join(', ');
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  getDeviceLanguage() {
    return window.navigator.language;
  }

  setTransactionDotState(show: boolean) {
    this.showPendingTransactionDot = show;
  }

  validateEmailAndPhone(loginId: string): any{
    let response = { isEmail: false, isPhoneNumber: false };
        if(this.emailRegex.test(loginId))
       response.isEmail = true;
    else if(this.mobileRegex.test(loginId))
       response.isPhoneNumber = true;
   return response;
  }

  formatLoginId(loginId: string): string{
    return loginId.replace(/^(0+)/g, '').replace('+','').trim();
  }

  getMessageError(err: any): string {
    let message: string = 'ErrorConnection';
    let response = err.error.ModelState ? err.error.ModelState : {};
    for (let key in response) {
      if (response.hasOwnProperty(key)) {
        message = 'Error_' + response[key][0];
      }
    }
    return message;
  }

  async lastPhonelogin(loginId: string){
    let loginUser = loginId.split("-");
    let user = loginUser[1] ? loginUser[1] : loginUser[0];
    return {'user': user};
  }

  async updateLastLogin(loginId: string, countryPrefix?: string){
    try {
      let lastlogin = await this.storageProvider.get(StorageType.LastLogin);
      if(!lastlogin)
        lastlogin = loginId;

      let lastLogin = lastlogin.split("@");

      if(!lastLogin[1]){
        let phoneLogin = await this.lastPhonelogin(loginId);
        await this.storageProvider.set(StorageType.LastLoginCountry, countryPrefix);
        await this.storageProvider.set(StorageType.LastLogin, phoneLogin.user);
      }else{
        await this.storageProvider.set(StorageType.LastLogin, loginId);
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Unable to update last login');
    }
  }

  arrayMove(arr, fromIndex, toIndex){
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
  }

  orderFieldWithParent(fieldList){
    let a = fieldList.filter(x => (x.fieldModel && x.fieldModel.Parent != undefined));

    while(a.length != 0){
      let fromIndex = fieldList.findIndex( x => (x.fieldModel && x.fieldModel.Parent != undefined && a[0].id == x.id));

      if(fromIndex < 0)
        break;

      let toIndex = fieldList.findIndex( x => (fieldList[fromIndex] && fieldList[fromIndex].fieldModel && x.id == fieldList[fromIndex].fieldModel.Parent));
      toIndex = toIndex < fromIndex ? toIndex + 1 : toIndex == fromIndex ? -1 : toIndex;

      if(toIndex > -1){
        this.arrayMove(fieldList, fromIndex, toIndex);
      }
      a.splice(0,1);
    }
  }

  scroll(input: ElementRef, content: Content, extraPixels, screenHeight?: number) {
    const inputPoisition = input.nativeElement.offsetTop + input.nativeElement.offsetHeight;
    let contentHeight = screenHeight ? screenHeight : content.contentHeight;
    if (contentHeight - 300 < inputPoisition) {
      const keyboardPosition = contentHeight - 300;
      const diff = inputPoisition - keyboardPosition;
      content.scrollTo(0, diff + extraPixels);
    }
  }

  replaceAmountAccordingLanguageNewOrder(amount: any, decimalPlaces?: number) {
    if(!(amount > 0)){
      return this.deviceLanguage.includes('en') ? '0.00' : '0,00'
    }
    if (typeof amount == 'string') {
      let decimals = this.getNumDecimals(amount);
      let numDecimals = Math.min(decimals,decimalPlaces);
      amount = new Intl.NumberFormat(this.deviceLanguage.includes('en') ? this.deviceLanguage.includes('en') : 'es-ES', {minimumFractionDigits: numDecimals, maximumFractionDigits: numDecimals}).format(Number(amount));
    }
    return amount;
  }

  getNumDecimals(num: any){
    let decimal  = num.split('.')[1];
    return decimal ? decimal.length : 0;
  }

  async blacklistedOTPRegistration(currentCountry){
    let countriesList = await this.formProvider.getPhoneNumberCountriesRegistration();
    return this.isValidPhoneBlacklistedByCategory(countriesList, currentCountry);
  }

  async blacklistedOTPFieldsCustomer(currentCountry){
    let countriesList = await this.formProvider.getPhoneNumberCountries();
    return this.isValidPhoneBlacklistedByCategory(countriesList, currentCountry);
  }

  isValidPhoneBlacklistedByCategory(countriesList, currentCountry){
    let country = countriesList.find(country => country.category == currentCountry);
    return country.isBlacklistedOTP;
  }

  async continueRegistrationPage(params, needToBePushed: Boolean = false) {
    await this.checkIsLiteRegistrationSettingEnabled();

    if (this.getIsLiteRegistrationEnabled())
      this.routeProvider.push(this.routeProvider.pages.RlCreateAccountPage, params)
    else {
      let page = this.routeProvider.pages.RegistrationStepUsernamePage;
      if (APP_CONFIG.region.oldRemittanceLogin) {
        page = this.routeProvider.pages.RegistrationStep1Page;
        this.formProvider.getFieldsRegistration(false, true); // reload fields if country has changed
        /**
        * registration-finish always creates agent referral (ARP) accounts through
        * api/Account/v2/Create, which requires ProvidedFields, so a referral has to continue into
        * the CST dynamic steps instead of the legacy RegistrationStep2Page chain - the legacy chain
        * only fills the flat fields and the endpoint answers "Required fields are missing".
        * Supply the referral specific required fields the same way registration-deeplinking-step
        * does: without fieldsPromise the dynamic page falls back to the generic field set, which
        * puts the address on a different step (so the Loqate gate never turns on) and never runs
        * updatePostalCodeRegex (so the postal code regex stays stale and rejects valid input).
        */
        const agentReferralToken = await this.storageProvider.get(StorageType.AgentReferralToken);
        if (agentReferralToken)
          params = {
            ...params,
            nextPage: this.routeProvider.pages.RegistrationStepDynamicPage,
            nextPageParams: {
              ...((params && params.nextPageParams) || {}),
              fieldsPromise: this.getAgentReferralRequiredFields(agentReferralToken),
            },
          };
      }
      if(needToBePushed)
        this.routeProvider.push(page, params)
      else
        this.routeProvider.replace(page, params)
    }    
  }

  /**
  * Mirrors RegisterProvider.getAgentReferralCustomerFields. It is duplicated here because
  * RegisterProvider already injects UtilsProvider, so injecting it back would be circular.
  */
  private async getAgentReferralRequiredFields(token: string): Promise<any> {
    const res = await this.httpProvider.get(`${APP_CONFIG.API_URL}api/Customer/NewCustomerRequiredFields`, {}, null, {
      AgentReferralToken: token
    });
    UtilsProvider.updatePostalCodeRegex(res);
    return res;
  }

  async checkLiteRegistrationSetting() {
    return this.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.LITE_REGISTRATION);
  }

  async checkIsLiteRegistrationSettingEnabled(){
    // hard code walmart exception to disable lite registration and enforece old remittance login
    if (APP_CONFIG.branding.branding == Branding.Walmart) {
      this.disableLiteRegistration = true;
      APP_CONFIG.region.oldRemittanceLogin = true;
    }

    if (APP_CONFIG.region.defaultCountryFrom === 'MY') {
      this.setIsLiteRegistrationEnabled(APP_CONFIG.liteRegisterationMY);
      return;
    }
    
    /**
    * The AgentReferralToken is set for anonymous QR code agent referrals
    * If the user has it, they scanned one previously and didn't register or login
    * We have to disable lite registration in this case
    */
    const agentReferralToken = await this.storageProvider.get(StorageType.AgentReferralToken);
    if (agentReferralToken) {
      this.disableLiteRegistration = false;
    }

    if (this.disableLiteRegistration)
    {
      this.setIsLiteRegistrationEnabled(false);
      return;
    }

    try{
      const setting = await this.checkLiteRegistrationSetting();
      this.setIsLiteRegistrationEnabled(setting == COUNTRY_SETTINGS_ENABLE_VALUE);
    }catch(ex){
      this.setIsLiteRegistrationEnabled(false);
    }
  }

  getIsLiteRegistrationEnabled(){
    return this.isLREnabled;
  }

  getRegistrationFlowType(){
    if(APP_CONFIG.region.region === Region.SN){
      return SegmentLocationNames.Wallet;
    }
    if (this.routeProvider.hasPage(this.routeProvider.pages.RegistrationDeeplinkingWelcomePage) || this.routeProvider.hasPage(this.routeProvider.pages.RegistrationDeeplinkingPreparePage))
      return SegmentLocationNames.FxOnlineDeeplinkRegistrationFlow;
    // exception: this case that we use parts of lite and full registration only happens in the fallback flow for the fx online deeplink registration (so after OTP we find out that the phone number has an open referral)
    if (this.routeProvider.hasPage(this.routeProvider.pages.RlCreatePhoneNumberPage) && this.routeProvider.hasPage(this.routeProvider.pages.RegistrationStepDynamicPage)){
      return SegmentLocationNames.FxOnlineDeeplinkRegistrationFlow;
    } else {
      return this.getIsLiteRegistrationEnabled() ? SegmentLocationNames.LiteRegistrationFlow : SegmentLocationNames.FullRegistrationFlow;
    }
  }

  getIsFxDeeplinkShowCountry(type: string){
    return this.deeplinkRegistrationFlowType ? this.deeplinkRegistrationFlowType : type;
  }

  setIsLiteRegistrationEnabled(value: boolean){
    this.isLREnabled = value;
  }

  availableLanguageCultures(){
    let languages = LANGUAGES;
    let availableLanguages: any[] = [];
    for (const lang in languages) {
      availableLanguages.push(lang);
    }
    return availableLanguages;
  }

  getFallbackLanguage(targetLang){
    if (targetLang) {
      let targetLangCopy = targetLang;
      targetLang = targetLang.substring(0,2);
      let languages = LANGUAGES;
      for (const lang in languages) {
        if (lang && lang.indexOf(targetLang) === 0){
          if(targetLangCopy === "en-US" || targetLangCopy === "en-CL"){
            return "en-US";
          } else if(targetLangCopy.indexOf("en") !== -1){
            return "en-GB";
          } else if(targetLangCopy.indexOf("es") !== -1){
            if(targetLangCopy === "es-US"){
              return "es-US";
            } else {
              return "es-ES";
            }
          } else if(targetLangCopy.indexOf("fr") !== -1){
            if(targetLangCopy === "fr-CA"){
              return "fr-CA";
            }else if(targetLangCopy === "fr-SN"){
              return "fr-SN";
            } else {
              return "fr-FR";
            }
          } else {
            return lang;
          }
        }
      }
    }
    return APP_CONFIG.region.defaultLanguageCulture; // ensure setUserCountry was called before to have the RIGHT region
  }

  async getSelectedCountry(){
    let country = await this.storageProvider.get(StorageType.UserCountry);
    if (!country) country = APP_CONFIG.region.defaultCountryFrom;
    return country;
  }

  openModalOTP(otpObject: OTPHeaderResponse,data): Promise<any> {
    const [email, phoneNo] = this.getEmailAndPhoneNo(data.username);
    return new Promise((resolve, reject) => {
    const analyticEvent: AnalyticsPhoneEventsVerification = {
      location: PHONE_VERIFICATION_EVENT_LOCATION.LOGIN,
      phoneNumber: phoneNo,
      phoneCountry: data.selectedCountry,
      email:email,
      registrationFlowType:"Login"
    }
    if(!data.selectedCountry) delete analyticEvent.phoneCountry;
    let modal = this.modal.create(PAGES.OTP_SCREEN, {
      otpPageType: OTP_ACTIONS.LOGINTOKEN, 
      ...otpObject,
      availableDeliveryTypes: [DELIVERY_TYPE.SMS, DELIVERY_TYPE.EMAIL, DELIVERY_TYPE.VOICE],
      emailOTP: otpObject.maskedEmail ? true : false,
      analyticEvent: analyticEvent,
      analyticEventLocation: PHONE_VERIFICATION_EVENT_LOCATION.LOGIN
    });
    modal.present();
    modal.onWillDismiss(() => {
      this.loadingProvider.show();
    });
    modal.onDidDismiss (signInResponse => {
      resolve(signInResponse);
    });
  });
}

  getPostOfficeValidator() {
    const regex = /P([.]?(O|0)?[.]?|ost|ostal).((O|0)ffice.)?Box{1}\b/i;
    return (control: FormControl) => {
      if (regex.test(control.value))
        return { 'invalidAddress': true };
      return null;
    };
  }

  checkPostOfficeError(control) {
    if (control.errors) {
      const errors = Object.keys(control.errors);
      if(errors.find(error => error === 'invalidAddress')) return 'page.registrationStep3Page.POBoxRegex';
    }
    return null;  
  }

  getEmailAndPhoneNo(userName){
    let [email, phoneNo] = ['', '']
    if (userName && userName.match(PATTERNS.EMAIL))
      email = userName;
    else if (userName && userName.match(PATTERNS.PHONE)) {
      const pos = userName.indexOf('-')
      if (pos > 0)
        phoneNo = userName.substr(pos + 1, userName.length);
    }
    return [email, phoneNo]
  }

  paramsToObject(entries) {
    const result = {}
    entries.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  static updatePostalCodeRegex(res, isRegistration = true){
    try {

      if (isRegistration){
        if(res.Value && res.Value.length > 0){
          let postalField = null;
          res.Value.forEach (elements => {
            elements.Fields.forEach(element => {
              if (element.FieldId == 4801){
                postalField = element;
                return;
              }
            });
          });
          if (postalField){
            postalField.FieldValidations.forEach(element => {
              if (element.Type === "Regex"){
                APP_CONFIG.region.postalCodeRegex = element.Value;
                return;
              }
            });
          }
        }
      }else{
        if(res && res.length > 0){
          let postalField = null;
          res.forEach (element => {
              if (element.id == "PostalCode"){
                postalField = element;
                return;
              }
          });
          if (postalField){
            postalField.validators.forEach(element => {
              if (element.name === "PostalCodeRegex"){
                APP_CONFIG.region.postalCodeRegex = element.regex;
                return;
              }
            });
          }
        }
      }
    } catch (error) {
      LoggerProvider.warning(this, LoggerEvent.GeneralException, 'Failed to set dynamic postal code regex provided in NewCustomerRequiredFields API');
    }
  }

  removeEmojisFromString(string){
    if(string && string.length > 0){
      var regex = /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;
      return string.replace(regex, '');
    }
    return '';
  }

  getFormattedActivityTransactionDate(dateFormat: string): string {
    let formattedDate = '';
    if (dateFormat) {
      const inputDate = new Date(dateFormat);
      const today = new Date();
      const inputYear = inputDate.getFullYear();
      const currentYear = today.getFullYear();
      const inputMonth = inputDate.getMonth();
      const currentMonth = today.getMonth();
      const inputDay = inputDate.getDate();
      const currentDay = today.getDate();
      const locale = RmtTranslateComponent.getRedirectLanguage();

      // Checking if the input date is today
      if (inputYear === currentYear && inputMonth === currentMonth && inputDay === currentDay) {
        return this.translate.instant('page.activityPage.todayLabel');
      }

      // Formatting the date based on whether it's in the current year or a previous year
      if (inputYear === currentYear) {
        formattedDate = inputDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
      } else {
        formattedDate = inputDate.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
      }
    }
    return formattedDate;
  }

  getFormattedDate(date) {
    const monthsTranslatedFull = this.translate.instant('global.myRia.jsonContent.MonthListFull');
    const monthsFull: string[] = monthsTranslatedFull.split(',');

    const month = date.getMonth();
    const day = date.getDate();
    const year = date.getFullYear();

    let dateFormat = '';
    if (APP_CONFIG.region.dateFormat) {
      dateFormat = APP_CONFIG.region.dateFormat;
      if (dateFormat.startsWith('YYYY'))
        dateFormat = dateFormat.substring(2);
    }

    let formatted = '';
    if (dateFormat.startsWith('YY'))
      formatted += year + ',';
    else if (dateFormat.startsWith('DD'))
      formatted += day;
    else
      formatted += monthsFull[month].trim();

    if (dateFormat.substring(3, 5) === 'MM')
      formatted += ' ' + monthsFull[month].trim();
    else
      formatted += ' ' + day;

    if (dateFormat.startsWith('YY')) {
      if (dateFormat.substring(6, 8) === 'MM')
        formatted += ' ' + monthsFull[month].trim();
      else
        formatted += ' ' + day;
    } else
      formatted += ', ' + year;
    
    return formatted
  }

  static isEUCountry(country){
    return (APP_CONFIG.region.isEu == true ||  country == "RO");

  }

  showUnableToRegisterPageBasedOnLocation(replace: boolean = true, primaryButtonAction = null, onBackCallback = null){
    if (!primaryButtonAction)
      primaryButtonAction = () => { 
        this.routeProvider.popTo(this.routeProvider.pages.PreLoginPage) 
      };
    let params = {
      showHelp: false,
      showBack: true,
      img: 'assets/icons/warn-icon.svg', 
      title: this.translate.instant('page.userCountryPage.unableToRegisterHeader'), 
      msg: this.translate.instant('page.userCountryPage.unableToRegisterBody'), 
      primaryButtonText: this.translate.instant('global.gotItButton'),
      primaryButtonAction,
      onBackCallback
    };
    this.routeProvider.pushOrReplace(replace, this.routeProvider.pages.InfoPage, params);
  }

  async checkIsEmailAlreadyRegistered(email, captchaSession, registrationFlowType, location:string = null){
    const res = await this.accountProvider.userRegistered(email, REGISTRATION_RESOURCE_TYPE.EMAIL, captchaSession);
    if(res.RetVal != 1){
      this.analytics.trackEvent({eventName: AnalyticsEventType.PasswordCreationFailed,
        analyticsData: {
          location: SegmentLocationNames.Registration,
          registrationFlowType: registrationFlowType,
          reason: 'Email In Use',
      }});  

      this.openIsEmailRegisteredModal(email, location);
      return true;
    }
    return false;
  }
  
  openIsEmailRegisteredModal(email, location: string) {
    const params = {
      header: this.translate.instant('page.registrationFailedPage.title'),
      texts: [
        this.translate.instant('form.registrationForm.emailAddress.duplicateEmail'),
      ],
      primaryButton: this.translate.instant('page.registrationFailedPage.genericLogInButton'),
      isTextCenter: true,
    }
    this.routeProvider.openActionSheet(this.routeProvider.pages.ActionsheetInfoPage, params, fromButton => { 
      this.redirectToLogin(fromButton, email);

      if(location && location == SegmentLocationNames.Registration){
        this.analytics.trackEvent({ eventName: AnalyticsEventType.LoginAccessed, analyticsData: {location: location} });
      }
      
      if(fromButton)this.analytics.trackEvent({ eventName: AnalyticsEventType.LoginStarted, analyticsData: {
        location: SegmentLocationNames.Registration,
        loginMethod: 'email',
        pilotUser: SegmentProvider.isPilotUser
      }});
    }, 'rmt-actionsheet-info-modal'); 
  }

  redirectToLogin(fromButton, email) {
    if(fromButton) 
      this.routeProvider.setRoot(this.routeProvider.pages.SigninPage, {username: email});
  }

  openEmailMarketingSubscriptionModal(noHandler, yesHandler){
    const confirm = this.alertCtrl.create({
      cssClass: "marketingConfirm",
      title: this.translate.instant(
        "page.registrationStep2Page.emailSignUpHeader") + '<img src = "./../../assets/icons/gift.svg">',
      message: this.translate.instant("page.registrationStep2Page.emailSignUpContent"),
      buttons: [
        {
          text: this.translate.instant(
            "form.registrationForm.notNowButton"
          ),
          cssClass: 'marketingButtonNo',
          handler: noHandler
        },
        {
          text: this.translate.instant(
            "page.registrationStep2Page.emailSignUpButton"
          ),
          cssClass: 'marketingButtonYes',
          handler: yesHandler
        }
      ]
    });
    confirm.setMode("ios");
    confirm.present();
  }

  highlightMatchSearchResult(searchResult, searchTerm) {

    if (searchTerm) {
      const regex = new RegExp(searchTerm, "gi");
      const strMatch = searchResult.match(regex);
      let searchResultName = searchResult;
      if (strMatch && strMatch.length > 0) {
        searchResultName = searchResultName.replace(strMatch[0], this.highlightMatchedString(strMatch[0]));
        searchResultName = this.highlightMatchedResult(searchResultName);
      } else {
        searchResultName = this.highlightMatchedResult(searchResultName);
      }
      return searchResultName;
    }

  }

  highlightMatchedResult(searchResultName) {
    return `<span class="search-result-item">${searchResultName}</span>`;
  }

  highlightMatchedString(searchTerm) {
    return `<span class="search-result-item-highlight">${searchTerm}</span>`
  }
  
  getDateInNumbers(){
    let currentDateAndTime = new Date();
    let month:any = currentDateAndTime.getMonth()+1;
    let date = currentDateAndTime.getDate() <= 9 ? "0" + currentDateAndTime.getDate() : currentDateAndTime.getDate();
    month =  month <= 9 ? "0" +  month : month;
    let parsedDate = currentDateAndTime.getFullYear() + ""+ month +""+ date +""+ currentDateAndTime.getHours()
                    +""+ currentDateAndTime.getMinutes() +""+ currentDateAndTime.getSeconds() +""+ currentDateAndTime.getMilliseconds();
    return parsedDate;
  }

  /**
   * Opens the prompt that asks a lite user to create a profile. The location goes into the "Create Profile Started" event.
   * After the in-app profile flow, the customer goes back to callBackPage, or to the recipients tab when callBackPage is the recipient list.
   */
  async openLiteRegActionSheetPage(callBackPage, location = '') {
    const enableEkycSingpass = await this.isSingpassRegistrationEnable();
    this.rmt5VueBridgeSettings = UtilsProvider.getFeatureFlagDataUnsafe(FeatureFlagTypes.Rmt5VueBridgeSettings);

    this.routeProvider.openActionSheet(this.routeProvider.pages.LiteRegActionSheetPage, null,
      (redirect: boolean) => { 
        if(redirect) {
          this.logCreateProfileStartedEvent(location)
          if(APP_CONFIG.region.region == Region.SG && enableEkycSingpass){
              this.routeProvider.openModal(this.routeProvider.pages.SingpassProfileVerifyMethodSelectPage, { pageLoadFrom: EKYC_PAGEFROM.REGISTER, location: location});
          } else if(this.rmt5VueBridgeSettings) {
            VueBridgeState.setUserInsideVueBridge(true);
            this.vueProvider.navigate('CompleteProfileInfo', '', {}, () => {
              // Fallback to in-app flow if Vue navigation fails
              console.log('Vue navigation failed, falling back to in-app flow');
              VueBridgeState.setUserInsideVueBridge(false);
              this.routeProvider.push(this.routeProvider.pages.RlDynamicFieldsPage, {
                location: location,
                onContinue: () => this.returnAfterLiteReg(callBackPage)
              });
            }, true); // Show loading while bridge connects
            if (VueBridgeState.isUserInsideVueBridge) {
              setTimeout(() => {
                this.routeProvider.pop();
              }, 500);
            }
          } else {
            this.routeProvider.push(this.routeProvider.pages.RlDynamicFieldsPage, {
              location: location,
              onContinue: () => this.returnAfterLiteReg(callBackPage)});
          }
        }
      }, 'rmt-actionsheet-modal-lowHeader', true);
      this.setActionSheetInfoStyles("0px", "0px", "0px", "0px");
  }

  private returnAfterLiteReg(callBackPage: INavInfo) {
    if (callBackPage.page === this.routeProvider.pages.RecipientListPage.page) {
      this.routeProvider.setRoot(this.routeProvider.pages.TabsHomeRecipientListPage);
    } else {
      this.routeProvider.popTo(callBackPage);
    }
  }

  async isSingpassEnable(){
    const enableEkycSingpassSetting = await this.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.ENABLE_EKYC_SINGPASS);
    return (enableEkycSingpassSetting == 'true' || enableEkycSingpassSetting == '1') ? true : false;
  }

  async isSingpassRegistrationEnable(){
    const enableEkycSingpassSetting = await this.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.ENABLE_SINGPASS_REG);
    return (enableEkycSingpassSetting == 'true' || enableEkycSingpassSetting == '1') ? true : false;
  }

  compareNumbers(number1, number2) {
    if (typeof number1 !== 'string')
      number1 = number1.toString();
    if (typeof number2 !== 'string')
      number2 = number2.toString();
    return (number1 === number2);
  }

  compareAmounts(amount1, amount2) {
    return this.compareNumbers(amount1, amount2);
  }

  isNumber(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }
  
  cleanPhoneNumber(phoneNumber){
    let cleanValue = phoneNumber.replace(/[ \-()+]/g, '');
    return cleanValue;
  }

  showAppStoreReviewPrompt(){
    try {
      this.analytics.trackEvent({ eventName: AnalyticsEventType.ProductReviewStarted, analyticsData: { reviewType: 'native' }});
      LoggerProvider.info(this, "AppRate SetPreferences Started" + AppRate);
      AppRate.setPreferences({
        reviewType: {
          ios: 'InAppReview',
          android: 'InAppReview'
        },
        simpleMode: true,
        showPromptForInAppReview: false,
        storeAppURL: {
            ios: '1065921908',
            android: 'market://details?id=com.ria.moneytransfer'
        }
      });
      LoggerProvider.info(this, "AppRate SetPreferences Completed" + AppRate);
      AppRate.promptForRating(true);
    } catch (error) {
      LoggerProvider.error(this, LoggerEvent.ExceptionMessage, "AppRate initialization failed" + AppRate);
    }
  }

  getLocalDayAsString(): string {
    const now = new Date();
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return days[now.getDay()];
  }

  getLocalHour(): number {
    const now = new Date();
    return now.getHours();
  }


  getTranslationInEnglish(key: string): string {
    const currentLang = this.translate.currentLang;
    let translatedReason = '';
    if (currentLang !== 'en_US') {
      this.translate.use('en_US');
    }
    translatedReason = this.translate.instant(key);
    this.translate.use(currentLang);
    return translatedReason;
  }

  hideCountryField(){ // TODO: Hiding the country field from address dyncamic fields to avoid breaking the flows. later check with BE is it something that can be driven from BE.
    setTimeout(() => {
      if(document.getElementById("countrySpan4901") && document.getElementById("countrySpan4901") !== null){
        document.getElementById("countrySpan4901").hidden = true;
      }  
    }, 200);
  }

  checkAndReturnLoginMethod(userName){
    const isEmailOrPhone = this.validateEmailAndPhone(userName);
    const loginMethod = isEmailOrPhone.isEmail === true ? "email": "phone";
    return loginMethod;
  }

  logCreateProfileStartedEvent(location){
    this.analytics.trackEvent({ eventName: AnalyticsEventType.CreateProfileStarted, analyticsData: {location: location}});
  }

  collectBrowserInfo() {
    const screenWidth = window && window.screen ? window.screen.width : '';
    const screenHeight = window && window.screen ? window.screen.height : '';
    // EMV 3DS only allows browserColorDepth 1|4|8|15|16|24|32|48. 
    const allowedColorDepths = [48, 32, 24, 16, 15, 8, 4, 1];
    const colorDepth = window && window.screen ? (allowedColorDepths.find((depth) => depth <= window.screen.colorDepth) || 24) : '';
    const userAgent = window && window.navigator ? window.navigator.userAgent : '';
    const javaEnabled = window && window.navigator ? navigator.javaEnabled() : false;

    let language = '';
    if (window && window.navigator) {
        language = window.navigator.language
        /*
            ? window.navigator.language
            : window.navigator.browserLanguage; // Else is for IE <+ 10
        */
    }

    const d = new Date();
    const timeZoneOffset = d.getTimezoneOffset();

    const browserInfo = {
        acceptHeader: '*/*',
        screenWidth,
        screenHeight,
        colorDepth,
        userAgent,
        timeZoneOffset,
        language,
        javaEnabled,
    };

    return browserInfo;
  }

  addAnalyticsKeyToInputFields(componentName: string, elementRef: ElementRef, renderer: Renderer2) {
    const inputElements = elementRef.nativeElement.querySelectorAll('input, ion-input');

    inputElements.forEach((inputElement, index) => {
      const parentElement = inputElement.parentElement || null;
      const firstChildElement = parentElement && parentElement.firstElementChild ? parentElement.firstElementChild : null;
      const label = firstChildElement && firstChildElement.innerText ? firstChildElement.innerText : null;

      if (label) {
        const existingAttribute = inputElement.getAttribute('analytics-name');

        if (!existingAttribute) {
          const uniqueProperties = `${componentName}${label}${inputElement.type}`;
          const uniqueName = `input-${this.hashString(uniqueProperties)}`;
                  
          renderer.setAttribute(inputElement, 'analytics-name', uniqueName);
        } 
      }
    });
  }

  private hashString(input: string): string {
    let hash = 0; 
    let charCode;

    if (input.length === 0) {
      return hash.toString();
    }

    for (let index = 0; index < input.length; index++) {
      charCode = input.charCodeAt(index); 
      hash = (hash << 5) - hash + charCode;
      hash |= 0;
    }

    return hash.toString();
  }

  normalizeToBoolean(value: any): boolean {
    // Treat "1", 1, true, and "true" (case-insensitive) as enabled
    if (value === 1 || value === '1' || value === true || String(value).toLowerCase() === 'true') {
      return true;
    }
    return false;
  }

  removeUndefinedProps(obj) {
    Object.keys(obj).forEach(key => {
      if (obj[key] === undefined) {
        delete obj[key];
      }
    });
  }

  lithuaniaPersonalNumberCheckSum(personalNumber) {
    var b = 1, c = 3, d = 0, e = 0, i, digit;
    for (i = 0; i < 10; i++) {
      digit = parseInt(personalNumber[i]);
      d += digit * b;
      e += digit * c;
      b++; if (b == 10) b = 1;
      c++; if (c == 10) c = 1;
    }
    d = d % 11;
    e = e % 11;
    if (d < 10)
      return d;
    else if (e < 10)
      return e;
    else
      return 0;
  }

  setActionSheetInfoStyles(infoContentPaddingBottom, textPaddingBottom, footerMarginBottom, contentPaddingBottom){
    if(this.platform.is('ios')){
      setTimeout(() => {
        if(document.getElementById("actionSheetInfoContent")){
          document.getElementById("actionSheetInfoContent").style.paddingBottom = infoContentPaddingBottom;
        }
        if(document.getElementById("actionSheetInfoText")){
          document.getElementById("actionSheetInfoText").style.paddingBottom = textPaddingBottom;
        }
        if(document.getElementById("actionSheetInfoFooter")){
          document.getElementById("actionSheetInfoFooter").style.marginBottom = footerMarginBottom;
        }
        if(document.getElementById("actionSheetContent")){
          document.getElementById("actionSheetContent").setAttribute('style', 'padding-bottom:'+contentPaddingBottom+' !important');
        }
      }, 50);
    }
  }

  async ensureFeatureFlags() {
    await HttpProvider.featureFlagCountryPendingPromise;
  }

  async hasFeatureFlag(featureFlag: FeatureFlagTypes) {
    await this.ensureFeatureFlags();
    return this.hasFeatureFlagUnsafe(featureFlag);
  }

  // IMPORTANT: use this function only when using the async function hasFeatureFlag is not working well because of the async but keep in  mind that this function
  // has the risk of having the feature flags not fully loaded yet
  // Hence only use this function if it's not mission critical that the feature flags have been loaded already and hence might return false although the feature flag is enabled
  hasFeatureFlagUnsafe(featureFlag: FeatureFlagTypes) {
    return UtilsProvider.resolveFeatureFlagRaw(featureFlag);
  }

  async getFeatureFlagData(featureFlag: FeatureFlagTypes) {
    await this.ensureFeatureFlags();
    return UtilsProvider.getFeatureFlagDataUnsafe(featureFlag);
  }

  async getFeatureFlagList(featureFlag: FeatureFlagTypes): Promise<string[]> {
    await this.ensureFeatureFlags();
    return UtilsProvider.parseFeatureFlagList(this.hasFeatureFlagUnsafe(featureFlag));
  }

  static parseFeatureFlagList(value: any): string[] {
    if (!value)
      return [];

    const cacheKey = typeof value === 'string' ? value : JSON.stringify(value);
    if (cacheKey in UtilsProvider.featureFlagListCache)
      return UtilsProvider.featureFlagListCache[cacheKey].slice();

    let entries = value;
    if (typeof entries === 'string') {
      try {
        entries = JSON.parse(entries);
      } catch (ex) {
        LoggerProvider.info(this, 'Reading feature flag list as a plain list');
      }
    }
    if (typeof entries === 'string')
      entries = entries.split(',');
    if (!Array.isArray(entries)) {
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Feature flag list is not a list, no entry will match', value);
      UtilsProvider.featureFlagListCache[cacheKey] = [];
      return [];
    }

    const parsed = entries
      .filter((entry) => typeof entry === 'string')
      .map((entry) => entry.toUpperCase().trim())
      .filter((entry) => !!entry);
    if (parsed.length === 0)
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Feature flag list parsed to an empty list', value);

    UtilsProvider.featureFlagListCache[cacheKey] = parsed;
    return parsed.slice();
  }

  static getFeatureFlagDataExperiment(featureFlag: FeatureFlagTypes) {
    const featureFlagExperiment = UtilsProvider.getFeatureFlagDataUnsafe(featureFlag);
    // we will have the experiment data in the feature flag value with this format: "experimentName:experimentValue", so we need to split it and return only the experiment value
    if(featureFlagExperiment && typeof featureFlagExperiment === 'string' && featureFlagExperiment.includes('test_')){
      return UtilsProvider.getFeatureFlagDataUnsafe(featureFlagExperiment.replace('test_', '') as FeatureFlagTypes);
    } else if(featureFlagExperiment && typeof featureFlagExperiment === 'string' && featureFlagExperiment.includes('control_')) {
      return false;
    }
    else {
      return featureFlagExperiment;
    }
  }


  // IMPORTANT: use this function only when using the async function hasFeatureFlag is not working well because of the async but keep in  mind that this function
  // has the risk of having the feature flags not fully loaded yet
  // Hence only use this function if it's not mission critical that the feature flags have been loaded already and hence might return false although the feature flag is enabled
  static getFeatureFlagDataUnsafe(featureFlag: FeatureFlagTypes) {
    const value = UtilsProvider.resolveFeatureFlagRaw(featureFlag);
    if (value !== undefined && value !== null) {
      const strValue = value.toString().toLowerCase();
      if (strValue === 'true')
        return true;
      else if (strValue === 'false')
        return false;
      return value;
    }
    return null;
  }

  showSelfServiceModalAlert(
  img: string,
  title: string,
  msg: string,
  primaryButtonText: string,
  onPrimaryAction: () => void,
  onBackCallback: () => void
) {
  this.routeProvider.push(this.routeProvider.pages.ModalAlertPage, {
    showFullPage: true,
    img,
    title,
    msg,
    primaryButtonText,
    showCircle: false,
    newFooterStyle: true,
    newDesign: true,
    showRightClose: false,
    showHelp: false,
    showBack: false,
    primaryAction: () => {
      onPrimaryAction();
    },
    onBackCallback,
  });
}

  showGenericError(){
    this.routeProvider.openActionSheet(this.routeProvider.pages.ModalAlertPage, {
        title: this.translate.instant("page.hubPage.errorOops.header"), 
        subtitle: this.translate.instant("page.hubPage.errorOops.content"),
        primaryButtonText: this.translate.instant("global.okButton")
    }, null);
  }

  isConsentModeEnabled(){
    return (APP_CONFIG.region.isEu || APP_CONFIG.region.region === Region.US ||
      APP_CONFIG.region.region === Region.CA || APP_CONFIG.region.region === Region.CL || APP_CONFIG.region.region === Region.SN);
  }

  isGoogleOrApplepay(paymentMethod) {
    if(typeof paymentMethod !== 'object')
      return paymentMethod && (paymentMethod.toLowerCase() === 'googlepay' || paymentMethod.toLowerCase() === 'applepay')
    else 
      return paymentMethod && (paymentMethod.type.toLowerCase() === 'googlepay' || paymentMethod.type.toLowerCase() === 'applepay')
  }
  
  public isNumeric(value) {
    return /^\d+$/.test(value);
  }

  setDeeplinkRegisterType(type: string){
    this.deeplinkRegistrationFlowType = type;
    this.setTrackRegistrationStartedEvent(type);
  }

  setDeeplinkImpactRadius(){
    this.deeplinkImpactRadiusName = 'ImpactRadius';
  }

  getDeeplinkImpactRadius(){
    return this.deeplinkImpactRadiusName;
  }

  setTrackRegistrationStartedEvent(regFlowType = ''){
    this.analytics.trackEvent({
      eventName: AnalyticsEventType.RegistrationStarted, analyticsData: {
        location: SegmentLocationNames.Landing,
        country: APP_CONFIG.region.region,
        registrationFlowType: this.getIsFxDeeplinkShowCountry(regFlowType),
        pilotUser: SegmentProvider.isPilotUser,
        ...getSourceAnalyticsData()
      }
    });
  }

  redirectToRestrictedTransfers() {
    this.routeProvider.openModal(this.routeProvider.pages.RestrictedTransfers, {
      primaryButtonAction: () => {
        this.routeProvider.push(this.routeProvider.pages.ReEnableTransfersPage);
      },
      secondaryButtonAction: async () => {
        this.loadingProvider.show();
  
        try {
          await this.navigateToHomeOrFallback(this.routeProvider.pages.TabsHomeActivityPage);
        } finally {
          this.loadingProvider.hide();
        }
      }
    });
  }
 
  /**
   * @description Function used to give format given value with the given pattern (patter should be created with #)
   * @param value: number | string
   * @param pattern: string
   * @returns string
   * @example formatValueWithPattern(1234567890, '###-###-####') => '123-456-7890'
   */
  public formatValueWithPattern(value: number | string, pattern: string): string {
    if (value === null || value === undefined)
      return '';

    // If value is an email, return as is
    if (typeof value === 'string' && value.includes('@')) {
      return value;
    }

    if (!pattern)
      return value.toString();
    
    const cleaned = value.toString().replace(/\D/g, '');
    let formatted = '';
    let index = 0;
    
    for (const char of pattern) {
        if (char === '#') {
        formatted += index < cleaned.length ? cleaned[index] : '';
        index++;
        } else {
        formatted += char;
        }
    
        if (index >= cleaned.length) break;
    }
    
    return formatted;
  }

  isAppleOrGooglePaymentCreditAllowed() {
    return APP_CONFIG.region.adyenMerchantCapability.indexOf('credit') > -1;
  }
  checkNonDgigitCharacters(value) {
    return /[a-zA-Z._]/.test(value)
  }

  checkDigitRegex(value) {
    return /^[0-9+\- .()]+$/.test(value);
  }

  static capitalizeFirstLetter(val) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }

  isAppleOrGooglePaymentDebitAllowed() {
    return APP_CONFIG.region.adyenMerchantCapability.indexOf('debit') > -1
  }


  mapPhoneCountryCode(code: number | string): string {
    if (typeof code === 'string') {
      const parsedNumber = parseInt(code);
      code = isNaN(parsedNumber) ? 0 : parsedNumber;
    }
    // Google returns the phone code as an Integer. Codes like '1' map to more
    // than one country. This logic needs to be refined to handle that case.
    // As for now, it will take the last occurrence of in the regions object.
    const region = APP_CONFIG.region;
    let phoneCountryCode = region.defaultCountryFrom;
    if (code !== 0 && code !== region.defaultCountryCodeFrom) {
      Object.keys(regions)
            .forEach(key => {
              if (regions[key].defaultCountryCodeFrom === code) {
                phoneCountryCode = regions[key].defaultCountryFrom;
              }
            });
    }
    return phoneCountryCode;
  }

  getSUBFromGoogleIdToken(token: string | null = null): string {
    if (!token) {
      return "";
    }
    const decoded = jwtDecode(token);
    return decoded.sub ? decoded.sub : "";
  }

  fetchRemoteImage(url: string, callback: (safeUrl: SafeUrl) => void) {
    LoggerProvider.info(this, `Fetching remote image: ${url}`);
    this.getImageAsBlob(url).subscribe(
      (response: Blob) => {
        const safeUrl = this.sanitizer.bypassSecurityTrustUrl(
          URL.createObjectURL(response)
        );
        LoggerProvider.info(this, `Remote image fetched: ${safeUrl}`);
        callback(safeUrl);
      },
      (error) =>
        LoggerProvider.error(this, LoggerEvent.ProviderException, error)
    );
  }

  private getImageAsBlob(url: string): Observable<{} | Blob> {
    return this.httpClient.get(url, { responseType: "blob" }).pipe(
      retryWhen((errors) => errors.pipe(delay(1500), take(2))),
      catchError(this.handleImageBlobError)
    );
  }

  private handleImageBlobError(error: HttpErrorResponse) {
    LoggerProvider.error(this, LoggerEvent.ProviderException, error.message);
    return Observable.create((observer: any) =>
      observer.error("Something went wrong fetching remote image blob.")
    );
  }
  
  static iCreditFeesCheck(item, value = null, networkCheck: string = 'debit') {
    let isHidden = false;
    if (APP_CONFIG.region.adyenMerchantCapability.indexOf(networkCheck) > -1) {
      if (value) {
        if (value === PaymentMethodType.ApplePayCredit || value === PaymentMethodType.GooglePayCredit) {
          isHidden = true;
        }
      } else if (item) {
        if (item.paymentMethod === PaymentMethodType.ApplePayCredit || item.paymentMethod === PaymentMethodType.GooglePayCredit) {
          isHidden = true;
        }
      }
    }
    return isHidden;
  }

  transformIfNotNullOrUndefined(value: any, currencyCode: string): string {
    return value !== null && value !== undefined
      ? this.currencyFormatter.transform(value, currencyCode, false)
      : "";
  }

  getAmountWithCurrency(amount: string | number, currencyCode: string): string {
    if (!amount || !currencyCode) return '';
    return amount + ' ' + currencyCode
  }

  // Common method to fire multiple promises at once as we don't have allSettled out of the box in ES2015
  allSettled<T>(promises: Promise<T>[]): Promise<{ status: string; value?: T; reason?: any }[]> {
  return Promise.all(
    promises.map(p =>
      p
        .then(value => ({ status: PromiseStatus.FULFILLED, value }))
        .catch(reason => ({ status: PromiseStatus.REJECTED, reason }))
    )
  );
  }

  async checkLithuaniaCountryLicense() {
    const setting = await this.hasCountrySettingByPromise(APP_CONFIG.region.regionId, COUNTRY_SETTINGS.DIGITAL_COUNTRY_LICENSE);
    return setting === DIGITAL_LICENSE.LITUANIA;
  }

  static extractAnalyticsErrorDetails(ex: any, defaultReason: string): AnalyticsErrorDetails {
    let err: any = ex;
    let failureReason = (err && err.error && err.error.retMsg) ? err.error.retMsg
      : (err && err.response && err.response.data && err.response.data.title) ? err.response.data.title
      : (err && err.message) ? err.message : defaultReason;
    let errorCode = (err && err.response && err.response.data && err.response.data.status != null) ? String(err.response.data.status)
      : (err && err.code) ? err.code : '';
    return { failureReason: failureReason, errorCode: errorCode };
  }

  static getResultRetVal(response: any): string {
    if (!response) {
      return '';
    }
    if (response.error && response.error.Result && response.error.Result.RetVal != null) {
      return String(response.error.Result.RetVal);
    }
    if (response.Result && response.Result.RetVal != null) {
      return String(response.Result.RetVal);
    }
    return '';
  }

}

export interface AnalyticsErrorDetails {
  failureReason: string;
  errorCode: string;
}

export interface AnalyticsErrorDetails {
  failureReason: string;
  errorCode: string;
}



// WEBPACK FOOTER //
// ./src/providers/myria/utils-provider.ts